import { useState } from 'react';
import styled from 'styled-components';

interface LocationsDisplayProps {
  locations: string;
  maxLength?: number;
}

const LocationsDisplay = ({ locations, maxLength = 25 }: LocationsDisplayProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Parse locations and determine display logic
  const locationList = locations
    ? locations
        .split(',')
        .map((loc) => loc.trim())
        .filter((loc) => loc.length > 0)
    : [];

  const locationCount = locationList.length;
  const hasMultipleLocations = locationCount > 1;

  // Determine display text
  let displayText;
  if (!locations || locations === 'All Locations') {
    return <span>All Locations</span>;
  } else if (hasMultipleLocations) {
    displayText = `${locationCount} locations`;
  } else if (locations.length > maxLength) {
    displayText = `${locations.substring(0, maxLength)}...`;
  } else {
    return <span>{locations}</span>;
  }

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <LocationContainer>
      <ClickableText onClick={toggleExpanded} expanded={isExpanded}>
        {displayText}
        <ExpandIcon expanded={isExpanded}>{isExpanded ? '▲' : '▼'}</ExpandIcon>
      </ClickableText>
      {isExpanded && (
        <LocationsList>
          {locationList.map((location, index) => (
            <LocationItem key={index}>• {location}</LocationItem>
          ))}
        </LocationsList>
      )}
    </LocationContainer>
  );
};

export default LocationsDisplay;

const LocationContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ClickableText = styled.span<{ expanded: boolean }>`
  cursor: pointer;
  color: ${({ theme }) => theme.text || '#333'};
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  padding: 2px 4px;
  border-radius: 3px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.widget_group?.background || '#f5f5f5'};
  }

  ${({ expanded, theme }) =>
    expanded &&
    `
    background-color: ${theme.widget_group?.background || '#f5f5f5'};
  `}
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  font-size: 10px;
  color: ${({ theme }) => theme.text || '#666'};
  opacity: 0.7;
  transition: transform 0.2s ease;
  margin-left: 2px;
`;

const LocationsList = styled.div`
  margin-top: 8px;
  padding: 8px;
  background: ${({ theme }) => theme.widget?.background || '#fff'};
  border: 1px solid ${({ theme }) => theme.border_color || '#e0e0e0'};
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-size: 12px;
  line-height: 1.4;
`;

const LocationItem = styled.div`
  color: ${({ theme }) => theme.text || '#333'};
  padding: 2px 0;
  word-wrap: break-word;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.border_color || '#f0f0f0'};
    padding-bottom: 4px;
    margin-bottom: 4px;
  }
`;
